import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Question from './question';
import MsxIcons from '../features/msx-icons';
import I10n from '../features/i10n';

const Container = styled.div`

  display: flex;
  flex-direction: column;
  padding: 10px 15px;
  user-select: none;
  background-color: #ffffff;
`;

const TitleWrapper = styled.div`
  display : flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  text-decoration: none !important;
`;

const CategoryTitle = styled.h4`
  margin: 0;
  color: #333333;
  font-size: 14px !important;
  line-height: normal !important;
  font-weight: bold;
  text-align: left !important;
`;

const HeaderRightPart = styled.div`
  display: flex;
  align-items: center;
`;

const NumberAnswerChip = styled.div`
  display: inline-flex;
  padding-left: 8px;
  padding-right: 8px;
  font-weight: 400;
  text-align: left;
  align-items: center;
  margin: 4px;
  outline: none;
  border-radius: 45px;

  font-size: 12px;
  line-height: 18px;
  color: black;
  background-color: #ecedf1;
`;

const IconWrapper = styled.span`
  display: inline-block;
  color: black;
`;

const QuestionsWrapper = styled.div`
  margin-top: 10px;
`;

const SelectedCategoryHeader = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const SelectedCategoryLine = styled.div`
  display: flex;
  flex: 1 1 0px;
  border-bottom: 1px solid #626469;
  height: 1px;
  margin-left: 10px;
`;
const ArrowCircle = styled.div`
  box-sizing: content-box;
  border: 1px solid black;
  border-radius: 50%;
  margin-left: 10px;
  user-select: none;
  overflow: hidden;
  width: 18px;
  height: 18px;
`;

// CSS hack to center the small arrow (which has natural untouchable padding).
const ArrowWrapper = styled.div`
  transition: transform 0.3s;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  width: 18px;
  height: 18px;
  box-sizing: content-box;
  display: flex;
  justify-content: center;
  align-items: center;
`;

class QuestionCategory extends React.Component {

  static propTypes = {
    questionCategory: PropTypes.object,
    onAnswerChanged: PropTypes.func,
    questionsWithAnswers: PropTypes.object,
    questionMap: PropTypes.object,
    onOpenStateChange: PropTypes.func,
    shouldBeOpened: PropTypes.bool,
    remainingOptions: PropTypes.object,
    sharedOptions: PropTypes.object,
    baseOptions: PropTypes.object,
    previousRemainingOptions: PropTypes.object,
    resultsLoading: PropTypes.bool,
    scrollbarWrapper: PropTypes.object
  }

  constructor(props) {
    super(props);
    this.state = {
      isOpen: this.categoryShouldBeOpenedOnStart(),
    }
  }

  /**
   * Returns true if the category should be opened at startup, i.e an answer is selected
   * in this category, or if the parent constraint it.
   */
  categoryShouldBeOpenedOnStart() {
    let shouldBeOpened = this.props.shouldBeOpened;
    for (const questionKey of Object.keys(this.props.questionsWithAnswers)) {
      if (this.props.questionMap
        && this.props.questionMap[questionKey]
        && this.props.questionMap[questionKey].groupKey === this.props.questionCategory.groupKey) {
        shouldBeOpened = true;
      }
    }
    return shouldBeOpened;
  }

  _getPreviousQuestionsAnswers = (question) => {
    const questionsWithAnswers = this.props.questionsWithAnswers;
    if (questionsWithAnswers[question?.previousQuestionKey]) {
      return questionsWithAnswers[question?.previousQuestionKey];
    }
    return null;
  }

  _getPreviousSharedQuestionAnswers = (question) => {
    const sharedOptions = this.props.sharedOptions;
    if (this.props.questionMap[question?.previousQuestionKey] && sharedOptions[this.props.questionMap[question?.previousQuestionKey].criteria]) {
      return sharedOptions[this.props.questionMap[question?.previousQuestionKey].criteria];
    }
    return null;
  }

  _questionHasOptions = (question) => {
    if (this.props.remainingOptions
      && this.props.remainingOptions[question.criteria]) {
      const remainingOptions = this.props.remainingOptions[question.criteria];
      const questionOptions = question.options.map(q => q.criteriaValue);
      const intersection = remainingOptions.filter(value => questionOptions.includes(value));
      return intersection.length > 0;
    }
    return true;
  }

  _questionShouldBeActive = (question) => {
    const havePreviousTrigger = !!question?.previousQuestionKey && question.previousQuestionsTrigger?.length > 0;
    if (!havePreviousTrigger) {
      return true;
    }
    const previousQuestionsAnswers = this._getPreviousQuestionsAnswers(question) || [];
    const previousQuestionsTrigger = question.previousQuestionsTrigger || [];
    const answersAndTriggerIntersection = previousQuestionsTrigger.filter(value => previousQuestionsAnswers.includes(value));

    let shouldBeActive = answersAndTriggerIntersection.length > 0;
    if (this.props.sharedOptions) {
      const previousSharedOptions = this._getPreviousSharedQuestionAnswers(question) || [];
      const sharedAndTriggerIntersection = previousQuestionsTrigger.filter(value => previousSharedOptions.includes(value));
      shouldBeActive = shouldBeActive || sharedAndTriggerIntersection.length > 0;
    }

    return shouldBeActive;
  }

  renderQuestions = (questions) => {
    return questions.map(question => {
      return (
        <Question
          key={question.questionKey}
          question={question}
          onAnswerChanged={this.props.onAnswerChanged}
          answerGiven={
            this.props.questionsWithAnswers[question.questionKey] ? this.props.questionsWithAnswers[question.questionKey] : null
          }
          remainingOptions={this.props.remainingOptions}
          sharedOptions={this.props.sharedOptions}
          baseOptions={this.props.baseOptions}
          resultsLoading={this.props.resultsLoading}
          scrollbarWrapper={this.props.scrollbarWrapper}
        >
        </Question>
      );
    });
  }

  get numberQuestionsForCategoryAnswered() {
    let nb = 0;
    this.props.questionCategory.questions.forEach((question) => {
      if (this.props.questionsWithAnswers[question.questionKey] != null) {
        nb++;
      }
    });
    return nb;
  }


  render = () => {
    const enabledQuestions = this.props.questionCategory.questions
      .filter(question => this._questionShouldBeActive(question) && this._questionHasOptions(question));
    return (
      enabledQuestions.length > 0 ?
        <Container>
          <TitleWrapper onClick={(e) => { e.preventDefault(); this.setState({ isOpen: !this.state.isOpen }); this.props.onOpenStateChange(); }}>
            <CategoryTitle>{I10n.getOrDefault(`question_groups.${this.props.questionCategory.groupKey}.label`, this.props.questionCategory.group, 'data')}</CategoryTitle>
           
            <SelectedCategoryLine></SelectedCategoryLine>
            <HeaderRightPart>
              {(!this.state.isOpen && this.numberQuestionsForCategoryAnswered > 0) && <NumberAnswerChip>
                {I10n.getAndReplace(`question.number_answer_filled${this.numberQuestionsForCategoryAnswered === 1 ? '.1' : ''}`, this.numberQuestionsForCategoryAnswered)}
              </NumberAnswerChip>}
              <SelectedCategoryHeader onClick={this.toggleSelectedLicenceDropdown}></SelectedCategoryHeader>
              {this.state.isOpen && <IconWrapper> <ArrowCircle>
                      <ArrowWrapper isOpen={this.state.isOpen}>
                        <MsxIcons name="down-arrow" size="nano"></MsxIcons>
                      </ArrowWrapper>
                    </ArrowCircle></IconWrapper>}
              {!this.state.isOpen && <IconWrapper> <ArrowCircle>
                      <ArrowWrapper isOpen={this.state.isOpen}>
                        <MsxIcons name="down-arrow" size="nano"></MsxIcons>
                      </ArrowWrapper>
                    </ArrowCircle></IconWrapper>}
            </HeaderRightPart>
          </TitleWrapper>
          {this.state.isOpen &&
            <QuestionsWrapper>
              {this.renderQuestions(enabledQuestions)}
            </QuestionsWrapper>
          }
        </Container>
        :
        <></>
    );
  }
}

export default QuestionCategory;
